import { AfterViewInit, Component, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { DefaultValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { Coordinate } from 'src/app/module/google-map/interface/google-map.interface';
import { GoogleMapService } from 'src/app/module/google-map/service/google-map.service';

@Component({
  selector: 'profet-coordinate',
  templateUrl: './coordinate.component.html',
  styleUrls: ['./coordinate.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: DefaultValueAccessor,
      multi: true,
    },
  ],
})
export class CoordinateComponent implements OnInit, AfterViewInit, OnDestroy {
  formCtrl!: FormControl;

  // Subscription to unsubscribe all observables on component destroy
  onDestroyNotifier$ = new Subject<void>();

  @Input() hint: string | undefined;
  @Input() hidden: boolean = false;
  @Input() required: boolean = false;

  @HostBinding('class.hidden') get hideClass() {
    return this.hidden;
  }

  constructor(
    public ngControl: NgControl,
    private googleMapService: GoogleMapService
  ) {}

  @HostListener('click', ['$event'])
  @HostListener('keydown.enter', ['$event'])
  @HostListener('keydown.space', ['$event'])
  onTrigger(event?: Event) {
    if (event) {
      event.preventDefault?.();
      event.stopPropagation?.();
    }
    this.open();
  }

  ngOnInit(): void {
    this.formCtrl = new FormControl(this.modelCtrlValue);
    // Disable the control if it is disabled in the parent form
    if (this.ngControl.status === 'DISABLED') this.formCtrl.disable();
  }

  ngAfterViewInit(): void {
    this.ngControl.statusChanges
      ?.pipe(takeUntil(this.onDestroyNotifier$), distinctUntilChanged())
      .subscribe((status) => {
        if (status === 'DISABLED') this.formCtrl.disable({ emitEvent: false });
        else this.formCtrl.enable({ emitEvent: false });
      });

    this.ngControl.control?.valueChanges
      .pipe(takeUntil(this.onDestroyNotifier$), distinctUntilChanged())
      .subscribe((value) =>
        this.formCtrl.setValue(value?.lat && value?.lng ? `${value.lat}, ${value.lng}` : null, { emitEvent: false })
      );

    this.formCtrl.valueChanges.pipe(takeUntil(this.onDestroyNotifier$), distinctUntilChanged()).subscribe((value) => {
      this.modelCtrlValue = value;

      if (this.formCtrl.errors) {
        this.ngControl.control?.setErrors({ ...this.formCtrl.errors });
      } else {
        this.ngControl.control?.setErrors(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  /** Mirrors the bound control's touched state onto the inner input so its error shows and clears with it. */
  syncValidationState(): void {
    if (this.ngControl.touched) this.formCtrl.markAsTouched();
    else this.formCtrl.markAsUntouched();

    this.formCtrl.updateValueAndValidity({ emitEvent: false });
  }

  open(): void {
    if (this.formCtrl.disabled) return;

    this.googleMapService
      .openLocationPicker(this.ngControl.control?.value)
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((coordinate) => {
        if (!coordinate) return;

        this.modelCtrlValue = coordinate;
      });
  }

  get modelCtrl(): FormControl {
    return this.ngControl.control as FormControl;
  }

  get modelCtrlValue(): Coordinate | null {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (this.ngControl.control as FormControl)?.value;
  }

  set modelCtrlValue(value: Coordinate | null) {
    if (this.ngControl.control) {
      this.ngControl.control.markAsDirty();
      this.ngControl.control.setValue(value);
    }
  }
}
